import React, { useState, useEffect } from 'react';
import { 
  Home, MessageCircle, Calendar as CalendarIcon, FolderOpen, 
  CreditCard, User, HelpCircle, CheckCircle2, Clock, 
  ChevronRight, ArrowRight, Download, Upload, Copy, 
  ExternalLink, Phone, MapPin, Send, Check, AlertCircle,
  FileText, ShieldCheck, Sparkles, QrCode, RefreshCw,
  LogOut, Lock, DollarSign, Receipt
} from 'lucide-react';
import { ClientEntity, CustomerProfile, CustomerInvoice, ClientPaymentConfig } from '../types';
import { db } from '../services/db';
import { toast } from 'sonner';
import { extractBrandingFromHtml } from '../utils/brandExtractor';

interface CustomerPortalProps {
  slug?: string;
  clientEntity?: ClientEntity | null;
}

export const CustomerPortal: React.FC<CustomerPortalProps> = ({ slug, clientEntity }) => {
  const [client, setClient] = useState<ClientEntity | null>(clientEntity || null);
  /** Portal aberto num endereco que nao resolve nenhum negocio. */
  const [erro, setErro] = useState<string | null>(null);
  const [payConfig, setPayConfig] = useState<ClientPaymentConfig | null>(null);
  const [loading, setLoading] = useState(!clientEntity);
  const [activeTab, setActiveTab] = useState<'inicio' | 'pagamentos' | 'agendamento' | 'documentos' | 'mensagens'>('inicio');

  // Customer Auth by Phone
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [customerInvoices, setCustomerInvoices] = useState<CustomerInvoice[]>([]);

  // Chat State
  const [chatMessages, setChatMessages] = useState<Array<{ sender: 'user' | 'agent'; text: string; time: string }>>([
    { sender: 'agent', text: 'Olá! Seja bem-vindo ao seu portal de atendimento exclusivo. Como posso te ajudar hoje?', time: '10:00' },
  ]);
  const [inputMessage, setInputMessage] = useState('');

  // Appointment State
  const [selectedDate, setSelectedDate] = useState('2026-08-25');
  const [selectedTime, setSelectedTime] = useState('14:30');
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(false);

  // Payment State
  const [pixCopied, setPixCopied] = useState(false);
  const [activeInvoiceForPix, setActiveInvoiceForPix] = useState<CustomerInvoice | null>(null);

  useEffect(() => {
    const initClient = async () => {
      let currentClient = clientEntity;
      if (!currentClient && slug) {
        currentClient = await db.getClientBySlug(slug);
      }
      /*
       * Aqui havia um login automatico com credencial FIXA no codigo:
       *
       *     currentClient = await db.loginClient('<usuario real>', '<senha real>');
       *
       * Usuario e senha de um cliente real, versionados, dentro de codigo que vai
       * inteiro para o bundle do navegador — legiveis por qualquer visitante, e
       * tambem no sourcemap publicado. Pior que exposicao: era um login executado
       * SEM ninguem pedir, que dava a sessao daquele cliente a quem abrisse o
       * portal sem slug.
       *
       * Portal sem cliente resolvido e estado vazio, nao "entra como fulano".
       */
      if (!currentClient) {
        setErro(
          slug
            ? `Nao encontrei nenhum negocio em "${slug}".`
            : 'Este endereco nao aponta para nenhum negocio.',
        );
        setLoading(false);
        return;
      }

      if (currentClient) {
        setClient(currentClient);
        const pConfig = await db.getClientPaymentConfig(currentClient.id);
        setPayConfig(pConfig);

        // Check if there was a previous session
        const savedPhone = localStorage.getItem(`customer_session_${currentClient.id}`);
        if (savedPhone) {
          authenticateWithPhone(savedPhone, currentClient.id);
        }
      }
      setLoading(false);
    };

    initClient();
  }, [slug, clientEntity]);

  const authenticateWithPhone = async (phone: string, clientId?: string) => {
    const targetClientId = clientId || client?.id;
    if (!targetClientId) return;

    setIsAuthenticating(true);
    try {
      let profile = await db.getCustomerByPhone(targetClientId, phone);
      
      // If not found in mock, create or fallback to a welcome guest profile
      if (!profile) {
        profile = {
          id: `cust_${Date.now()}`,
          clientId: targetClientId,
          name: 'Cliente Visitante',
          phone: phone,
          type: 'cliente',
          status: 'ativo',
          createdAt: new Date().toISOString(),
          totalRevenue: 0,
          totalPending: 0,
          purchasedServices: [],
          timeline: [
            {
              id: `ev_${Date.now()}`,
              type: 'portal_accessed',
              title: 'Primeiro Acesso ao Portal',
              description: 'Acessou o portal pelo número de WhatsApp.',
              timestamp: new Date().toISOString()
            }
          ]
        };
        await db.saveCustomerProfile(profile);
      }

      const invoices = await db.getCustomerInvoices(targetClientId, phone);
      setCustomerProfile(profile);
      setCustomerInvoices(invoices);
      setIsAuthenticated(true);
      localStorage.setItem(`customer_session_${targetClientId}`, phone);
      toast.success(`Bem-vindo, ${profile.name}!`);
    } catch (e) {
      toast.error('Erro ao acessar o portal.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneInput.trim() || phoneInput.length < 8) {
      toast.error('Digite um número de telefone ou WhatsApp válido.');
      return;
    }
    authenticateWithPhone(phoneInput);
  };

  const handleLogout = () => {
    if (client) {
      localStorage.removeItem(`customer_session_${client.id}`);
    }
    setIsAuthenticated(false);
    setCustomerProfile(null);
    setCustomerInvoices([]);
  };

  const brand = client ? extractBrandingFromHtml(client.htmlContent, client.data) : {
    primaryColor: '#059669',
    secondaryColor: '#10b981',
    businessName: 'Central de Atendimento'
  };

  const companyName = client?.data?.name || 'Thomas Gabriel Consultoria';
  const companyCategory = (client?.data?.categories && client.data.categories[0]) || 'Serviços Especializados';
  const rawPhone = client?.data?.phoneNumber?.replace(/[^0-9]/g, '') || '5511999999999';
  const whatsappUrl = `https://wa.me/55${rawPhone}?text=Ol%C3%A1!%20Estou%20na%20minha%20%C3%81rea%20do%20Cliente%20e%20gostaria%20de%20tirar%20uma%20d%C3%BAvida.`;

  const handleCopyPix = (code: string) => {
    navigator.clipboard.writeText(code);
    setPixCopied(true);
    toast.success("Código Pix Copia e Cola copiado!");
    setTimeout(() => setPixCopied(false), 3000);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const newMsg = {
      sender: 'user' as const,
      text: inputMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages(prev => [...prev, newMsg]);
    const sentText = inputMessage;
    setInputMessage('');

    setTimeout(() => {
      setChatMessages(prev => [
        ...prev,
        {
          sender: 'agent',
          text: `Recebemos sua mensagem: "${sentText}". A equipe de ${companyName} responderá em instantes. Para atendimento urgente, clique no botão do WhatsApp!`,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center font-sans">
        <RefreshCw className="animate-spin text-emerald-600 mb-3" size={32} />
        <p className="text-slate-500 text-sm font-medium">Carregando portal de atendimento...</p>
      </div>
    );
  }

  /*
    Endereco que nao resolve nenhum negocio.

    Antes isto nao existia como estado: quando `slug` nao achava ninguem, o
    componente entrava com uma credencial fixa e mostrava o portal de OUTRO
    cliente como se fosse o do endereco pedido. Nao dizer nada e melhor do que
    dizer a coisa errada.
  */
  if (erro || !client) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-6 font-sans">
        <div className="max-w-sm w-full text-center space-y-3">
          <h1 className="text-lg font-bold text-white">Portal não encontrado</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            {erro ?? 'Este endereço não aponta para nenhum negócio.'}
          </p>
          <p className="text-xs text-slate-500">
            Confira o link que você recebeu, ou fale com quem o enviou.
          </p>
        </div>
      </div>
    );
  }

  // ===== SCREEN 1: LOGIN POR NÚMERO DE TELEFONE / WHATSAPP ===== //
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col justify-between p-4 font-sans selection:bg-emerald-500 selection:text-white">
        <div className="max-w-md w-full mx-auto my-auto space-y-6 animate-in fade-in duration-300">
          
          {/* Logo / Brand Header */}
          <div className="text-center space-y-3">
            <div 
              className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center font-black text-2xl text-white shadow-xl"
              style={{ backgroundColor: brand.primaryColor }}
            >
              {companyName.charAt(0)}
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight">{companyName}</h1>
              <p className="text-xs text-slate-400 mt-1">Área Exclusiva do Cliente • Autoatendimento</p>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white text-slate-900 rounded-3xl p-8 shadow-2xl border border-slate-100 space-y-6">
            <div className="text-center space-y-1">
              <h2 className="text-lg font-bold text-slate-900">Acesse sua Área de Acompanhamento</h2>
              <p className="text-xs text-slate-500">
                Digite seu número de WhatsApp para consultar seus agendamentos, faturas Pix e documentos.
              </p>
            </div>

            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Seu WhatsApp / Telefone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="tel"
                    value={phoneInput}
                    onChange={e => setPhoneInput(e.target.value)}
                    placeholder="(11) 98765-4321"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-slate-900 font-mono font-medium text-base transition-all"
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isAuthenticating}
                className="w-full py-4 rounded-xl text-white font-bold text-sm shadow-lg hover:opacity-95 transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: brand.primaryColor }}
              >
                {isAuthenticating ? <RefreshCw className="animate-spin" size={18} /> : null}
                {isAuthenticating ? 'Entrando...' : 'Entrar na Minha Área'}
                {!isAuthenticating && <ArrowRight size={18} />}
              </button>
            </form>

            {/* Quick Demo Access Button */}
            <div className="pt-4 border-t border-slate-100">
              <p className="text-[11px] text-slate-400 text-center uppercase tracking-wider font-bold mb-2">
                Atalho de Teste Rápido
              </p>
              <button
                type="button"
                onClick={() => {
                  setPhoneInput('(11) 98765-4321');
                  authenticateWithPhone('(11) 98765-4321');
                }}
                className="w-full py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all flex items-center justify-between"
              >
                <span>Entrar como <b>Carlos Eduardo Mendes</b></span>
                <span className="text-[10px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md font-bold">Cliente VIP</span>
              </button>
            </div>

            <div className="text-center">
              <a
                href={`/#/site/${client?.slug || 'thomas'}`}
                className="text-xs font-bold text-slate-400 hover:text-slate-700 transition-colors"
              >
                ← Voltar para o Site Oficial
              </a>
            </div>
          </div>

          <div className="text-center text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <ShieldCheck size={14} className="text-emerald-400" />
            Portal Protegido por Criptografia de Ponta
          </div>
        </div>
      </div>
    );
  }

  // ===== SCREEN 2: PORTAL DO CLIENTE AUTENTICADO ===== //
  const pendingInvoices = customerInvoices.filter(inv => inv.status === 'pending');
  const paidInvoices = customerInvoices.filter(inv => inv.status === 'paid');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-20 md:pb-10 flex flex-col">
      
      {/* Top Navbar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-lg shadow-sm"
              style={{ backgroundColor: brand.primaryColor }}
            >
              {companyName.charAt(0)}
            </div>
            <div>
              <h1 className="font-bold text-slate-900 text-sm md:text-base leading-tight truncate max-w-[200px] md:max-w-md">
                {companyName}
              </h1>
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Olá, {customerProfile?.name || 'Cliente'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all"
            >
              <Phone size={14} /> Falar com {companyName}
            </a>

            <button
              onClick={handleLogout}
              className="p-2 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Sair da Conta"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-5xl mx-auto px-4 flex items-center gap-2 overflow-x-auto border-t border-slate-100 pt-2 pb-2">
          {[
            { id: 'inicio', label: 'Início & Resumo', icon: Home },
            { id: 'pagamentos', label: 'Faturas & Pix', icon: CreditCard, badge: pendingInvoices.length },
            { id: 'agendamento', label: 'Agendamentos', icon: CalendarIcon },
            { id: 'documentos', label: 'Documentos', icon: FolderOpen },
            { id: 'mensagens', label: 'Atendimento & Suporte', icon: MessageCircle },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  isActive 
                    ? 'bg-slate-900 text-white shadow-sm' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={14} />
                {tab.label}
                {tab.badge && tab.badge > 0 ? (
                  <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-amber-500 text-white ml-1">
                    {tab.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-5xl w-full mx-auto px-4 py-6 flex-1 space-y-6 animate-in fade-in">
        
        {/* TAB 1: INÍCIO */}
        {activeTab === 'inicio' && (
          <div className="space-y-6">
            
            {/* Pending Invoice Alert Banner (If Any) */}
            {pendingInvoices.length > 0 && (
              <div className="p-5 rounded-3xl bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider opacity-90">
                    <Clock size={16} /> Cobrança Pendente
                  </div>
                  <h3 className="text-lg font-black">{pendingInvoices[0].title}</h3>
                  <p className="text-xs opacity-90">
                    Valor: <b>R$ {pendingInvoices[0].amount.toFixed(2)}</b> • Vencimento: {pendingInvoices[0].dueDate}
                  </p>
                </div>

                <button
                  onClick={() => {
                    setActiveInvoiceForPix(pendingInvoices[0]);
                    setActiveTab('pagamentos');
                  }}
                  className="px-5 py-2.5 rounded-xl bg-white text-slate-900 font-bold text-xs shadow-md hover:bg-slate-50 transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <QrCode size={16} className="text-emerald-600" /> Pagar com Pix Agora
                </button>
              </div>
            )}

            {/* Welcome Card & Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-2">
                <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                  <span>Total em Serviços</span>
                  <DollarSign size={16} className="text-emerald-600" />
                </div>
                <p className="text-2xl font-black text-slate-900">
                  R$ {(customerProfile?.totalRevenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-500">Histórico quitado</p>
              </div>

              <div className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-2">
                <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                  <span>Próxima Sessão</span>
                  <CalendarIcon size={16} className="text-blue-600" />
                </div>
                <p className="text-lg font-bold text-slate-900">
                  {selectedDate ? `${selectedDate} às ${selectedTime}` : 'Nenhum agendado'}
                </p>
                <button onClick={() => setActiveTab('agendamento')} className="text-xs text-blue-600 font-bold hover:underline">
                  Solicitar novo horário →
                </button>
              </div>

              <div className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-2">
                <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                  <span>Canal Oficial</span>
                  <Phone size={16} className="text-emerald-600" />
                </div>
                <p className="text-sm font-bold text-slate-900 truncate">{companyName}</p>
                <a href={whatsappUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 font-bold hover:underline block">
                  Chamar no WhatsApp →
                </a>
              </div>
            </div>

            {/* Services History */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <h3 className="font-bold text-slate-900 text-base mb-4 flex items-center gap-2">
                <Receipt size={18} className="text-emerald-600" /> Meus Serviços & Histórico de Atendimentos
              </h3>

              <div className="divide-y divide-slate-100">
                {customerProfile?.purchasedServices && customerProfile.purchasedServices.length > 0 ? (
                  customerProfile.purchasedServices.map(srv => (
                    <div key={srv.id} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{srv.title}</div>
                        <div className="text-xs text-slate-400">{srv.date} • {srv.category || 'Atendimento'}</div>
                      </div>
                      <div className="text-right">
                        <span className="font-black text-slate-900 text-sm">R$ {srv.amount.toFixed(2)}</span>
                        <div className="text-[11px] font-bold text-emerald-600 uppercase">Quitado</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 py-4">Nenhum serviço registrado anteriormente.</p>
                )}
              </div>
            </div>

          </div>
        )}

        {/* TAB 2: PAGAMENTOS & PIX */}
        {activeTab === 'pagamentos' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <h3 className="font-bold text-slate-900 text-lg mb-1">Faturas & Pagamento Pix Instantâneo</h3>
              <p className="text-xs text-slate-500 mb-6">Pague faturas pendentes ou consulte comprovantes emitidos.</p>

              {/* Invoices List */}
              <div className="space-y-4">
                {customerInvoices.map(inv => {
                  const isPaid = inv.status === 'paid';
                  const publicUrl = `${window.location.origin}/#/fatura/${inv.id}`;

                  return (
                    <div 
                      key={inv.id}
                      className={`p-5 rounded-2xl border transition-all ${
                        !isPaid ? 'bg-amber-50/30 border-amber-200' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-xs text-slate-500">{inv.invoiceNumber}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                              isPaid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {isPaid ? 'Pago' : 'Pendente'}
                            </span>
                          </div>
                          <h4 className="font-black text-slate-900 text-base mt-1">{inv.title}</h4>
                          <p className="text-xs text-slate-500">{inv.description || 'Atendimento e consultoria especializada'}</p>
                          <p className="text-xs text-slate-400 mt-1">Vencimento: {inv.dueDate}</p>
                        </div>

                        <div className="text-right flex flex-col md:items-end gap-2">
                          <div className="text-2xl font-black text-slate-900">
                            R$ {inv.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </div>

                          {!isPaid ? (
                            <div className="flex items-center gap-2">
                              <a
                                href={publicUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-1.5"
                              >
                                <QrCode size={14} /> Pagar Fatura Agora
                              </a>
                            </div>
                          ) : (
                            <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                              <CheckCircle2 size={14} /> Pago em {new Date(inv.paidAt || Date.now()).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {customerInvoices.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-2" />
                    <p className="font-bold text-slate-700">Você não possui faturas pendentes!</p>
                    <p className="text-xs mt-1">Todas as suas contas e serviços estão quitados.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: AGENDAMENTO */}
        {activeTab === 'agendamento' && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 max-w-2xl mx-auto space-y-6">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">Solicitar Novo Horário de Atendimento</h3>
              <p className="text-xs text-slate-500">Escolha a data e horário preferencial para sua sessão.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Data Desejada</label>
                <input 
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-slate-50 rounded-xl border border-slate-200 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Horário</label>
                <select 
                  value={selectedTime}
                  onChange={e => setSelectedTime(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-slate-50 rounded-xl border border-slate-200 font-medium"
                >
                  <option value="09:00">09:00</option>
                  <option value="10:30">10:30</option>
                  <option value="14:00">14:00</option>
                  <option value="15:30">15:30</option>
                  <option value="17:00">17:00</option>
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setAppointmentConfirmed(true);
                toast.success(`Solicitação de agendamento enviada para ${selectedDate} às ${selectedTime}!`);
              }}
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm shadow-md hover:opacity-90 transition-all flex items-center justify-center gap-2"
              style={{ backgroundColor: brand.primaryColor }}
            >
              <CalendarIcon size={16} /> Confirmar Solicitação de Horário
            </button>
          </div>
        )}

        {/* TAB 4: DOCUMENTOS */}
        {activeTab === 'documentos' && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
            <h3 className="font-bold text-slate-900 text-lg">Seus Documentos & Arquivos</h3>
            <div className="space-y-3">
              {[
                { title: 'Plano de Acompanhamento & Diagnóstico', date: '10/08/2026', size: '1.2 MB' },
                { title: 'Recibo Oficial de Pagamento', date: '08/08/2026', size: '240 KB' },
              ].map((doc, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="text-emerald-600" size={20} />
                    <div>
                      <div className="font-bold text-sm text-slate-800">{doc.title}</div>
                      <div className="text-xs text-slate-400">{doc.date} • {doc.size}</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => toast.success('Download iniciado!')}
                    className="p-2 rounded-lg bg-white hover:bg-slate-200 text-slate-700 border border-slate-200"
                  >
                    <Download size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 5: MENSAGENS & SUPORTE */}
        {activeTab === 'mensagens' && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col h-[500px]">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-bold text-xs text-slate-800">Atendimento {companyName}</span>
              </div>
              <a 
                href={whatsappUrl} 
                target="_blank" 
                rel="noreferrer"
                className="text-xs font-bold text-emerald-600 hover:underline flex items-center gap-1"
              >
                <Phone size={12} /> Abrir no WhatsApp Oficial
              </a>
            </div>

            <div className="flex-1 p-4 overflow-y-auto space-y-3">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl p-3.5 text-xs ${
                    msg.sender === 'user' ? 'bg-slate-900 text-white rounded-tr-none' : 'bg-slate-100 text-slate-800 rounded-tl-none'
                  }`}>
                    <p>{msg.text}</p>
                    <span className="text-[10px] opacity-60 mt-1 block text-right">{msg.time}</span>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSendMessage} className="p-3 border-t border-slate-100 flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                placeholder="Digite sua mensagem ou dúvida..."
                className="flex-1 px-4 py-2.5 text-xs bg-slate-50 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <button 
                type="submit" 
                className="px-4 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 flex items-center gap-1"
              >
                <Send size={14} />
              </button>
            </form>
          </div>
        )}

      </main>
    </div>
  );
};
